import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, Calendar, X, Newspaper } from 'lucide-react'
import { PageHeader } from '@/components/sections/PageHeader'
import { Section } from '@/components/ui/Section'
import { Card, CardContent } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { newsArticles, getNewsTranslation, type NewsArticle } from '@/data/news'
import type { SupportedLanguage } from '@/i18n'
import { PageMeta } from '@/components/PageMeta'

export function NewsListPage() {
  const { t, i18n } = useTranslation('pages')
  const lang = i18n.language as SupportedLanguage

  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null)

  const filteredArticles = useMemo(() => {
    return newsArticles.filter((article) => {
      const data = getNewsTranslation(article, lang)
      const titleMatch = data.title.toLowerCase().includes(searchQuery.toLowerCase())
      const date = new Date(article.date)
      const fromMatch = !dateFrom || date >= new Date(dateFrom)
      const toMatch = !dateTo || date <= new Date(dateTo + 'T23:59:59')
      return titleMatch && fromMatch && toMatch
    })
  }, [searchQuery, dateFrom, dateTo, lang])

  const hasFilters = searchQuery || dateFrom || dateTo

  const resetFilters = () => {
    setSearchQuery('')
    setDateFrom('')
    setDateTo('')
  }

  return (
    <>
      <PageMeta title={`${t('news.title')} — FG Automazioni`} description={t('news.subtitle')} />
      <PageHeader
        title={t('news.title')}
        subtitle={t('news.subtitle')}
        tag={t('news.tag')}
        icon={Newspaper}
        gradient="amber"
      />
      <Section background="white">
        {/* Filter Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8 overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-900/5"
        >
          <div className="h-1.5 w-full bg-gradient-to-r from-[#f75e47] to-[#f75e47]/60" />
          <div className="p-6">
            <div className="grid gap-4 md:grid-cols-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder={t('news.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-4 text-sm transition-colors focus:border-[#f75e47] focus:outline-none"
                />
              </div>

              {/* Date From */}
              <div className="relative">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 py-2.5 pl-4 pr-4 text-sm transition-colors focus:border-[#f75e47] focus:outline-none"
                />
              </div>

              {/* Date To */}
              <div className="relative">
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 py-2.5 pl-4 pr-4 text-sm transition-colors focus:border-[#f75e47] focus:outline-none"
                />
              </div>
            </div>

            {/* Results count & Reset */}
            <div className="mt-4 flex items-center justify-between px-1">
              <p className="text-sm font-medium text-gray-700">
                {filteredArticles.length === 0
                  ? t('news.noResults')
                  : t('news.articleCount', { count: filteredArticles.length })}
              </p>
              <AnimatePresence>
                {hasFilters && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    onClick={resetFilters}
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:border-[#f75e47] hover:text-[#f75e47] transition-colors"
                  >
                    <X size={14} />
                    {t('news.resetFilters')}
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="py-16 text-center"
          >
            <div className="text-6xl mb-4">📰</div>
            <p className="text-lg text-gray-600">{t('news.emptyState')}</p>
            {hasFilters && (
              <Button variant="outline" onClick={resetFilters} className="mt-4">
                {t('news.resetFilters') || 'Reimposta filtri'}
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div
            layout
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            <AnimatePresence mode="popLayout">
              {filteredArticles.map((article) => {
                const data = getNewsTranslation(article, lang)
                return (
                  <motion.div
                    key={article.slug}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card
                      variant="elevated"
                      className="group flex flex-col overflow-hidden cursor-pointer border-2 border-transparent hover:border-[#f75e47]/30 transition-all duration-300"
                      onClick={() => setSelectedArticle(article)}
                    >
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={article.image}
                          alt={data.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute left-4 top-4">
                          <span className="inline-flex items-center rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-gray-900 shadow-sm">
                            <Calendar className="mr-1.5 h-3.5 w-3.5 text-[#f75e47]" />
                            {article.date}
                          </span>
                        </div>
                      </div>
                      <CardContent className="flex flex-1 flex-col">
                        <h2 className="text-lg font-bold tracking-tight text-gray-900 line-clamp-2 group-hover:text-[#f75e47] transition-colors">
                          {data.title}
                        </h2>
                        <p className="mt-3 flex-1 text-sm leading-relaxed text-gray-600 line-clamp-3">
                          {data.excerpt}
                        </p>
                        <div className="mt-4 flex items-center text-sm font-semibold text-[#f75e47]">
                          {t('news.readMore')}
                          <svg
                            className="ml-1.5 h-4 w-4 transform transition-transform group-hover:translate-x-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </motion.div>
        )}
      </Section>

      {/* Article Modal */}
      <Modal isOpen={!!selectedArticle} onClose={() => setSelectedArticle(null)}>
        {selectedArticle && (
          <div className="flex flex-col h-full flex-shrink-0">
            <div className="relative h-64 md:h-80 flex-shrink-0">
              <img
                src={selectedArticle.image}
                alt={getNewsTranslation(selectedArticle, lang).title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="text-sm text-white/70">{selectedArticle.date}</p>
                <h2 className="mt-1 text-2xl font-bold leading-tight">
                  {getNewsTranslation(selectedArticle, lang).title}
                </h2>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div
                className="text-gray-700 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{
                  __html: getNewsTranslation(selectedArticle, lang).content,
                }}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  )
}
